import MultiSelectorTarget from '@repo/common/components/MultiSelector/MultiSelectorTarget';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import type { ISecodaEntity } from '../../../api';
import { parsePropertyField } from '../../Properties/utils';
import { SecodaEntityIcon } from '../../SecodaEntity';

interface OptimizedRelatedEntitiesRenderProps {
	entity: ISecodaEntity & { related_entities?: ISecodaEntity[] };
	field?: string;
	label?: string;
	hideOnEmpty?: boolean;
}

/**
 * Lightweight read-only component to display related entities without selector functionality.
 * Used when user doesn't have permission to edit related entities.
 * Uses the abstracted MultiSelectorTarget for consistent entity display across the app.
 */
export function OptimizedRelatedEntitiesRender({
	entity,
	field = 'related_entities',
	label = 'resources',
	hideOnEmpty = true,
}: OptimizedRelatedEntitiesRenderProps) {
	const relatedEntityIds = useMemo(
		() => parsePropertyField(entity, field),
		[entity, field]
	);

	const selectedEntities = useMemo(() => {
		// If we have related_entities data directly on the entity, use it
		if (entity.related_entities) {
			return relatedEntityIds
				.map((entityId) =>
					entity.related_entities?.find((re) => re.id === entityId)
				)
				.filter((entity) => !isNil(entity))
				.map((entity) => ({
					label: entity!.title || 'Untitled',
					value: entity!.id,
					icon: <SecodaEntityIcon entity={entity!} size={20} />,
				}));
		}

		// If no related_entities data is available, return empty array
		// This avoids async fetching and keeps the component lightweight
		return [];
	}, [relatedEntityIds, entity.related_entities]);

	return (
		<MultiSelectorTarget
			selected={selectedEntities}
			placeholder={`No ${label}`}
			hideOnEmpty={hideOnEmpty}
			readOnly
			iconType="react-node"
			property="entity"
			propertyLabel={label}
			isMenuItemBadge={false}
			truncate
		/>
	);
}
